import React, { useState, useEffect } from "react";
import ImageGallery from "./ImageGallery";
import "../../scss/familyTree/memberEditor.scss";

/**
 * MemberEditor Component
 * Modal for editing family member details and managing images
 */
const MemberEditor = ({
  member,
  onSave,
  onCancel,
  onDelete,
  onImageSelect,
  availableGenerations = [],
}) => {
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    firstName: "",
    lastName: "",
    gender: "other",
    birthDate: "",
    deathDate: "",
    images: [],
    generation: 0,
  });

  const [showGallery, setShowGallery] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [addedImages, setAddedImages] = useState([]);
  const [originalImageIds, setOriginalImageIds] = useState(new Set());

  useEffect(() => {
    if (member) {
      setFormData({
        id: member.id || "",
        name: member.name || "",
        firstName: member.firstName || "",
        lastName: member.lastName || "",
        gender: member.gender || "other",
        birthDate: formatDateForInput(member.birthDate),
        deathDate: formatDateForInput(member.deathDate),
        images: member.images || [],
        generation: member.generation !== undefined ? member.generation : 0,
      });
      // Track original image IDs to prevent sending them as "new"
      const originalIds = new Set((member.images || []).map((img) => img.id));
      setOriginalImageIds(originalIds);
      // Reset tracking when member changes
      setRemovedImageIds([]);
      setAddedImages([]);
    }
  }, [member]);

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    } catch (e) {
      return dateString;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }

    if (formData.birthDate && formData.deathDate) {
      const birth = new Date(formData.birthDate);
      const death = new Date(formData.deathDate);
      if (birth > death) {
        newErrors.deathDate = "Death date must be after birth date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      // Start with base data (exclude images by default)
      const { images, ...baseData } = formData;

      const saveData = {
        ...baseData,
        name:
          formData.name?.trim() ||
          `${formData.firstName} ${formData.lastName}`.trim(),
      };

      // Only include images array if new images were actually added (send only new images)
      if (addedImages.length > 0) {
        saveData.images = addedImages;
      }

      // Only include removeImages for ORIGINAL images that were deleted
      // Filter out any IDs that were from newly added images
      const originalDeletedImages = removedImageIds.filter((id) =>
        originalImageIds.has(id)
      );
      if (originalDeletedImages.length > 0) {
        saveData.removeImages = originalDeletedImages;
      }

      console.log("MemberEditor - Saving with data:", {
        originalImageIds: Array.from(originalImageIds),
        addedImages: addedImages.map((img) => ({
          id: img.id,
          type: img.type,
          url: img.url?.substring(0, 50),
        })),
        removedImageIds: removedImageIds,
        originalDeletedImages: originalDeletedImages,
        finalPayload: saveData,
      });

      onSave(saveData);
    }
  };

  const handleAddImage = (image, allImages) => {
    console.log("MemberEditor - handleAddImage called", { image, allImages });

    // If allImages is provided (from toggle/type change), just update formData
    // This happens when toggling profile/gallery type - NOT adding new images
    if (allImages) {
      console.log(
        "MemberEditor - Updating all images (toggle/type change mode)"
      );
      setFormData((prev) => ({
        ...prev,
        images: allImages,
      }));
      // Don't track toggle operations as new images
      return;
    }

    // Only track truly new images (when a single image object is provided)
    // AND it's not an original image
    if (image && typeof image === "object" && image.id) {
      console.log("MemberEditor - Adding single new image", image.id);

      // Check if this image already existed in the original member data
      if (originalImageIds.has(image.id)) {
        console.log("MemberEditor - Image already exists, not tracking as new");
        return;
      }

      const newImage = { ...image };

      // Track the new image object for API payload (only truly new ones)
      setAddedImages((prev) => [...prev, newImage]);

      // Also add to formData for display purposes
      setFormData((prev) => ({
        ...prev,
        images: [...(prev.images || []), newImage],
      }));
    }
  };

  const handleDeleteImage = (imageToDelete) => {
    console.log("MemberEditor - Deleting image:", imageToDelete.id);

    // If this was a newly added image (not in original), remove it from addedImages
    if (!originalImageIds.has(imageToDelete.id)) {
      console.log("MemberEditor - Removing from newly added images");
      setAddedImages((prev) =>
        prev.filter((img) => img.id !== imageToDelete.id)
      );
    } else {
      // If this was an original image, track it for deletion
      console.log("MemberEditor - Tracking original image for deletion");
      setRemovedImageIds((prev) => [...prev, imageToDelete.id]);
    }

    // Remove from formData images for display
    setFormData((prev) => ({
      ...prev,
      images: (prev.images || []).filter((img) => img.id !== imageToDelete.id),
    }));
  };

  const handleDeleteMember = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (onDelete) {
      onDelete(formData.id);
    }
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Get profile image - find image with type="profile", fallback to first image or icon
  const profileImageObj =
    formData.images?.find((img) => img.type === "profile") ||
    formData.images?.[0];
  const profileImage = profileImageObj?.url || "👤";

  return (
    <>
      <div className="member-editor-modal">
        <div className="editor-overlay" onClick={onCancel} />

        <div className="editor-container">
          <div className="editor-header">
            <h2>Edit Member Details</h2>
            <button className="close-btn" onClick={onCancel}>
              ✕
            </button>
          </div>

          <div className="editor-content">
            {/* Profile Image Section */}
            <div className="profile-section">
              <div className="profile-image-container">
                {typeof profileImage === "string" &&
                profileImage.startsWith("http") ? (
                  <img src={profileImage} alt={formData.name} />
                ) : (
                  <div className="profile-icon">{profileImage}</div>
                )}
              </div>
              <button
                className="btn-gallery"
                onClick={() => setShowGallery(true)}
              >
                🖼️ Manage Images
              </button>
              <p className="image-count">
                {formData.images?.length || 0} image
                {formData.images?.length !== 1 ? "s" : ""}
              </p>
            </div>

            {/* Form Fields */}
            <form className="edit-form">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  className={errors.name ? "error" : ""}
                />
                {errors.name && (
                  <span className="error-message">{errors.name}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    id="firstName"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="First name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    id="lastName"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="gender">Gender</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                >
                  <option value="male">♂ Male</option>
                  <option value="female">♀ Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="birthDate">Birth Date</label>
                  <input
                    id="birthDate"
                    type="date"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="deathDate">Death Date (if applicable)</label>
                  <input
                    id="deathDate"
                    type="date"
                    name="deathDate"
                    value={formData.deathDate}
                    onChange={handleInputChange}
                    className={errors.deathDate ? "error" : ""}
                  />
                  {errors.deathDate && (
                    <span className="error-message">{errors.deathDate}</span>
                  )}
                </div>
              </div>
            </form>
          </div>

          <div className="editor-actions">
            <button
              className="btn-save"
              onClick={handleSave}
              title="Save Changes"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </button>
            <button className="btn-cancel" onClick={onCancel} title="Cancel">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            {onDelete && (
              <button
                className="btn-delete"
                onClick={handleDeleteMember}
                title="Delete Member"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  <path d="M10 11v6M14 11v6" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="delete-confirm-modal">
          <div className="confirm-overlay" onClick={cancelDelete} />
          <div className="confirm-dialog">
            <div className="confirm-header">
              <h3>⚠️ Confirm Deletion</h3>
            </div>
            <div className="confirm-content">
              <p>
                Are you sure you want to delete <strong>{formData.name}</strong>
                ?
              </p>
              <p className="warning-text">
                This action cannot be undone. All data associated with this
                member will be permanently deleted.
              </p>
            </div>
            <div className="confirm-actions">
              <button
                className="btn-confirm-delete"
                onClick={confirmDelete}
                title="Confirm Delete"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  <path d="M10 11v6M14 11v6" />
                </svg>
                <span>Yes, Delete</span>
              </button>
              <button
                className="btn-confirm-cancel"
                onClick={cancelDelete}
                title="Cancel"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showGallery && (
        <ImageGallery
          member={formData}
          images={formData.images}
          onClose={() => setShowGallery(false)}
          onAddImage={handleAddImage}
          onDeleteImage={handleDeleteImage}
        />
      )}
    </>
  );
};

export default MemberEditor;
